import type { ReactNode } from "react";
import { styled } from "styled-components";

import {
  DeleteIcon,
  EditIcon,
  LockIcon,
  MagicWandIcon,
  PlayIcon,
  RightArrow,
  ShareIcon,
} from "common/components/icons";
import { UIButton, type UIButtonProps, type Variant } from "common/components/UIButton";

type Icon = "rightArrow" | "lock" | "play" | "magicWand" | "share" | "edit" | "delete";

type UIIconButtonProps = UIButtonProps & {
  icon: Icon;
};

const StyledUIButton = styled(UIButton)`
  border-radius: 30px;
  height: 60px;
  padding: 0.5rem 0.5rem 0.5rem 1.5rem;
`;

const StyledIconContainer = styled.div<{ variant?: Variant }>`
  margin-left: 1rem;
  border-color: ${({ theme }) => theme.colors.buttonOutlinedText};
  border-width: 1px;
  border-style: solid;
  border-radius: 50%;
  padding: 12px;
  display: flex;
  border-color: ${({ theme, variant }) =>
    variant === "outlined"
      ? theme.colors.buttonOutlinedText
      : variant === "neutral"
        ? theme.colors.buttonNeutralText
        : theme.colors.white};
`;

const StyledPlayIcon = styled(PlayIcon)`
  // this is needed because the play icon itself is not square
  // by adding this to the width, we are centering it
  padding-left: 2px;
`;

const iconLookup: Readonly<Record<Icon, ReactNode>> = {
  delete: <DeleteIcon width={16} height={16} />,
  edit: <EditIcon width={16} height={16} />,
  lock: <LockIcon width={14} height={14} />,
  magicWand: <MagicWandIcon width={14} height={14} />,
  play: <StyledPlayIcon width={15} height={17} />,
  rightArrow: <RightArrow width={14} height={14} />,
  share: <ShareIcon width={16} height={16} />,
};

export const UIIconButton = ({ as, children, icon, variant, ...rest }: UIIconButtonProps) => (
  <StyledUIButton {...rest} forwardedAs={as} variant={variant}>
    {children}
    <StyledIconContainer variant={variant}> {iconLookup[icon]} </StyledIconContainer>
  </StyledUIButton>
);
